import _ from 'lodash';
import { properCase } from './helpers';
import BaseChartModel from './BaseChartModel';

const defaultOptions = {
    groupColumn: 'sex',
    leftGroup: 'Male',
    rightGroup: 'Female',
    yColumn: 'age',
    xColumn: 'value',
    ranges: [{
        min: 66,
        label: 'Over 65'
    }, {
        max: 17,
        label: 'Under 18'
    }],
    groupIncrement: 5,
    units: '%',
    format: value => `${(value * 100).toFixed(0)}`,
    tooltipFormat: value => `${(value * 100).toFixed(1)}`,
    rawValueFormat: value => value.toLocaleString('en-us')
};

/* eslint-disable no-underscore-dangle */
export default class PopulationChartModel extends BaseChartModel {
    constructor(dataRecords, parent, options) {
        super(dataRecords, parent, { ...options, ...defaultOptions });
        window.pcm = this;
    }

    get _records() {
        return this.rawDataRecords
            .filter(({ geographyCode }) => geographyCode === this.valueGeographyCode)
            .map(({
                [this.options.groupColumn]: group,
                [this.options.xColumn]: x,
                [this.options.yColumn]: y
            }) => ({ x, y, group }));
    }

    get _compareRecords() {
        return this.rawDataRecords
            .filter(({ geographyCode }) => geographyCode === this.comparisonGeographyCode)
            .map(({
                [this.options.groupColumn]: group,
                [this.options.xColumn]: x,
                [this.options.yColumn]: y
            }) => ({ x, y, group }));
    }

    _getRangeForValue({ y }) {
        return _.findIndex(this.options.ranges, ({ min, max }) => (min == null || y >= min) && (max == null || y <= max));
    }

    get values() {
        if (this._records.length === 0) {
            return [];
        }
        const grouped = _.groupBy(this._records, row => properCase(row.group));
        return [this.options.leftGroup, this.options.rightGroup].map(group => ({
            group: properCase(group),
            values: _(grouped[properCase(group)])
                // .map(record => ({ ...record, highlight: this._getRangeForValue(record) !== -1 }))
                .groupBy(({ y }) => Math.floor(parseInt(y, 10) / this.options.groupIncrement))
                .map(arr => ({
                    x: _.sumBy(arr, 'x'),
                    y: Math.min(...arr.map(({ y }) => parseInt(y, 10))),
                    label: arr.length > 1 ? `AGE ${_(arr).minBy(({ y }) => parseInt(y, 10)).y}-${_(arr).maxBy(({ y }) => parseInt(y, 10)).y}` : `AGE ${arr[0].y}`
                }))
                .sortBy(({ y }) => parseInt(y, 10))
                .map(({ x, y, label }, i, arr) => {
                    if (i === arr.length - 1) {
                        return { x, y, label: `${label}+` };
                    }
                    return { x, y, label };
                })
                .value()
        }));
    }

    get compareValues() {
        if (this._compareRecords.length === 0) {
            return [];
        }
        const grouped = _.groupBy(this._compareRecords, row => properCase(row.group));
        return [this.options.leftGroup, this.options.rightGroup].map(group => ({
            group: properCase(group),
            values: _(grouped[properCase(group)])
                // .map(record => ({ ...record, highlight: this._getRangeForValue(record) !== -1 }))
                .groupBy(({ y }) => Math.floor(parseInt(y, 10) / this.options.groupIncrement))
                .map(arr => ({
                    x: _.sumBy(arr, 'x'),
                    y: Math.min(...arr.map(({ y }) => parseInt(y, 10)))
                }))
                .sortBy(({ y }) => parseInt(y, 10))
                .value()
        }));
    }

    get proportionsInRanges() {
        if (this._records.length === 0) {
            return [];
        }
        const totalPop = arr => _.sumBy(arr, 'x');
        return this.options.ranges.map(({ label }, i) => ({
            label,
            value: totalPop(this._records.filter(record => this._getRangeForValue(record) === i))
                / totalPop(this._records),
            compareValue: totalPop(this._compareRecords.filter(record => this._getRangeForValue(record) === i))
            / totalPop(this._compareRecords),
        })).map(({ value, compareValue, ...rest }) => ({ value, compareValue, ...rest, valueString: this.options.format(value), compareValueString: `vs ${this.options.format(compareValue)}${this.units} ${this.parent.comparisonText}` }));
    }

    get units() {
        return this.options.units;
    }

    get localName() {
        return this.parent.localName;
    }

    get comparisonText() {
        return this.parent.comparisonText;
    }
}
